/**
 * Modelo para Solicitação de Agendamento de Visita / Visita sem Agendamento
 */
import { ehVisitaPorPeriodo as ehVisitaPorPeriodoFn } from "@/utils/visita-data";
import { isentaFluxoDigitalRecepcao as isentaFluxoDigitalRecepcaoFn } from "@/config/fluxo-digital-solicitacao";
import { normalizarVisitanteDocumentos, visitanteParaPayloadApi } from "@/config/visitante-documentos";

export default class SolicitacaoVisita {
  constructor(obj = {}) {
    // Dados do Anfitrião(ã)
    this.agendada = obj.agendada ?? null; // true = Sim, false = Não
    this.criadoPorUsuarioId = obj.criadoPorUsuarioId ?? null;
    this.nomeAnfitriao = obj.nomeAnfitriao ?? "";
    this.anfitriaoUsuarioId = obj.anfitriaoUsuarioId ?? null;
    this.areaDepartamento = obj.areaDepartamento ?? "";
    this.dataSolicitacao = obj.dataSolicitacao ?? null;

    // Dados do Visitante (visita individual: um; visita em grupo: vários)
    this.nomeCompleto = obj.nomeCompleto ?? "";
    const docsTopo = normalizarVisitanteDocumentos(obj);
    this.cpf = obj.cpf ?? docsTopo.cpf;
    this.rg = obj.rg ?? docsTopo.rg;
    /** @deprecated use rg — mantido para compatibilidade */
    this.documentoRgCnh = obj.documentoRgCnh ?? this.rg ?? "";
    this.empresaOrigem = obj.empresaOrigem ?? "";
    this.email = obj.email ?? "";
    this.telefoneWhatsapp = obj.telefoneWhatsapp ?? "";
    this.anexoDocumento = obj.anexoDocumento ?? null;
    this.fotoUrl = obj.fotoUrl ?? "";
    /** Lista de visitantes da mesma visita (grupo). Cada item: { nomeCompleto, documentoRgCnh, email, telefoneWhatsapp } */
    this.visitantes = obj.visitantes ?? null;

    // Tipo de Visitante (múltipla escolha)
    this.tipoCliente = obj.tipoCliente ?? false;
    this.tipoFornecedor = obj.tipoFornecedor ?? false;
    this.tipoPrestadorServico = obj.tipoPrestadorServico ?? false;
    this.tipoParceiros = obj.tipoParceiros ?? false;
    this.tipoOutros = obj.tipoOutros ?? false;
    this.tipoOutrosDescricao = obj.tipoOutrosDescricao ?? "";

    // Informações da Visita
    this.motivoVisita = obj.motivoVisita ?? "";
    this.observacaoVisita = obj.observacaoVisita ?? "";
    this.areaSetorVisitado = obj.areaSetorVisitado ?? "";
    this.dataVisita = obj.dataVisita ?? null;
    this.dataVisitaFim = obj.dataVisitaFim ?? null;
    this.horarioEntradaPrevisto = obj.horarioEntradaPrevisto ?? "";
    this.horarioSaidaPrevisto = obj.horarioSaidaPrevisto ?? "";
    this.quantidadeVisitantes = obj.quantidadeVisitantes ?? 1;

    // Necessidades Logísticas
    this.visitaraFabrica = obj.visitaraFabrica ?? false;
    this.salaReuniaoLite = obj.salaReuniaoLite ?? false;
    this.salaReuniaoHard = obj.salaReuniaoHard ?? false;
    this.cafe = obj.cafe ?? false;
    this.agua = obj.agua ?? false;
    this.paoQueijoBiscoito = obj.paoQueijoBiscoito ?? false;
    this.brinde = obj.brinde ?? false;

    // Status do agendamento (1 a 6)
    this.statusAgendamento = obj.statusAgendamento ?? 1;
    this.origem = obj.origem ?? null; // 'anfitriao' quando informado pelo anfitrião(ã)
    this.cancelada = obj.cancelada ?? false;
    this.canceladaEm = obj.canceladaEm ?? null;
    this.motivoCancelamento = obj.motivoCancelamento ?? "";
    /** Fluxo digital (após anfitrião(ã) informar): e-mail → presença → sala → logística */
    this.conviteEnviadoEm = obj.conviteEnviadoEm ?? null;
    this.informacoesPreenchidasEm = obj.informacoesPreenchidasEm ?? null;
    this.documentoConfidencialidadeRecebidoEm = obj.documentoConfidencialidadeRecebidoEm ?? null;
    this.confirmacaoPresencaVisitanteEm = obj.confirmacaoPresencaVisitanteEm ?? null;
    this.agendamentoSalaEfetuadoEm = obj.agendamentoSalaEfetuadoEm ?? null;
    this.necessidadesLogisticasEncaminhadasEm = obj.necessidadesLogisticasEncaminhadasEm ?? null;
    this.checkinEm = obj.checkinEm ?? null;
    this.checkoutEm = obj.checkoutEm ?? null;
    this.checkoutRegistradoPorNome = obj.checkoutRegistradoPorNome ?? null;
    this.id = obj.id ?? null;
    this.created_at = obj.created_at ?? null;
    this.updated_at = obj.updated_at ?? null;
  }

  /** True se foi informado pelo anfitrião(ã) (dados parciais) — recepcionista deve completar */
  get informadaPeloAnfitriao() {
    return this.origem === "anfitriao" || (this.nomeCompleto && !this.cpf && !this.rg && !this.empresaOrigem);
  }

  /**
   * Fluxo digital concluído: as quatro etapas atuais, ou registros legados com os três marcos antigos
   * (convite + informações + NDA) antes da etapa de logística.
   */
  get conviteCompleto() {
    if (isentaFluxoDigitalRecepcaoFn(this)) return true;
    const e1 = !!this.conviteEnviadoEm;
    const e2 = !!(this.confirmacaoPresencaVisitanteEm ?? this.informacoesPreenchidasEm);
    const e3 = !!(this.agendamentoSalaEfetuadoEm ?? this.documentoConfidencialidadeRecebidoEm);
    const e4 = !!this.necessidadesLogisticasEncaminhadasEm;
    if (e1 && e2 && e3 && e4) return true;
    if (
      e1 &&
      !!this.informacoesPreenchidasEm &&
      !!this.documentoConfidencialidadeRecebidoEm &&
      !e4
    ) {
      return true;
    }
    return false;
  }

  /** true se a visita abrange mais de um dia */
  get ehVisitaPorPeriodo() {
    return ehVisitaPorPeriodoFn(this.dataVisita, this.dataVisitaFim);
  }

  /** Dispensa o fluxo digital da recepção quando NÃO for cliente nem parceiro (cliente/parceiro exigem o fluxo) */
  get isentaFluxoDigitalRecepcao() {
    return isentaFluxoDigitalRecepcaoFn(this);
  }

  /** True se a visita tem mais de um visitante (check-in/check-out em grupo) */
  get ehVisitaEmGrupo() {
    const list = this.visitantes;
    return Array.isArray(list) && list.length > 1;
  }

  /** Lista de visitantes para exibição (sempre array; deriva do primeiro se não houver visitantes[]) */
  get listaVisitantes() {
    if (Array.isArray(this.visitantes) && this.visitantes.length > 0) {
      return this.visitantes.map((v) => {
        const docs = normalizarVisitanteDocumentos(v);
        return {
        nomeCompleto: v.nomeCompleto ?? v.nome_completo ?? "",
        cpf: docs.cpf,
        rg: docs.rg,
        documentoRgCnh: docs.rg,
        email: v.email ?? "",
        telefoneWhatsapp: v.telefoneWhatsapp ?? v.telefone_whatsapp ?? "",
        fotoUrl: v.fotoUrl ?? v.foto_url ?? v.foto ?? v.path_image ?? v.pathImage ?? "",
        visitanteId: v.visitanteId ?? v.visitante_id ?? null,
      };
      });
    }
    return [{
      nomeCompleto: this.nomeCompleto,
      cpf: this.cpf ?? "",
      rg: this.rg ?? "",
      documentoRgCnh: this.rg ?? this.documentoRgCnh ?? "",
      email: this.email,
      telefoneWhatsapp: this.telefoneWhatsapp,
      fotoUrl: this.fotoUrl,
      visitanteId: null,
    }];
  }

  toPayload() {
    const visitantesPayload = this.listaVisitantes.map((v) => {
      const docs = visitanteParaPayloadApi(v);
      const row = {
        nome_completo: v.nomeCompleto,
        ...docs,
        email: v.email,
        telefone_whatsapp: v.telefoneWhatsapp,
      };
      const vid = v.visitanteId;
      if (vid != null && Number(vid) > 0) {
        row.visitante_id = Number(vid);
      }
      return row;
    });
    return {
      agendada: this.agendada,
      criado_por_usuario_id: this.criadoPorUsuarioId,
      nome_anfitriao: this.nomeAnfitriao,
      anfitriao_usuario_id: this.anfitriaoUsuarioId,
      area_departamento: this.areaDepartamento,
      data_solicitacao: this.dataSolicitacao,
      nome_completo: this.nomeCompleto,
      cpf: this.cpf ?? "",
      documento_rg_cnh: this.rg ?? this.documentoRgCnh ?? "",
      empresa_origem: this.empresaOrigem,
      email: this.email,
      telefone_whatsapp: this.telefoneWhatsapp,
      visitantes: visitantesPayload,
      quantidade_visitantes: visitantesPayload.length,
      tipo_cliente: this.tipoCliente,
      tipo_fornecedor: this.tipoFornecedor,
      tipo_prestador_servico: this.tipoPrestadorServico,
      tipo_parceiros: this.tipoParceiros,
      tipo_outros: this.tipoOutros,
      tipo_outros_descricao: this.tipoOutrosDescricao,
      motivo_visita: this.motivoVisita,
      observacao_visita: this.observacaoVisita?.trim() ? this.observacaoVisita.trim() : null,
      area_setor_visitado: this.areaSetorVisitado,
      data_visita: this.dataVisita,
      data_visita_fim: this.ehVisitaPorPeriodo ? this.dataVisitaFim : null,
      horario_entrada_previsto: this.horarioEntradaPrevisto,
      horario_saida_previsto: this.horarioSaidaPrevisto,
      visitara_fabrica: this.visitaraFabrica,
      sala_reuniao_lite: this.salaReuniaoLite,
      sala_reuniao_hard: this.salaReuniaoHard,
      cafe: this.cafe,
      agua: this.agua,
      pao_queijo_biscoito: this.paoQueijoBiscoito,
      brinde: this.brinde,
      status_agendamento: this.statusAgendamento,
      origem: this.origem,
      cancelada: this.cancelada,
      cancelada_em: this.canceladaEm,
      motivo_cancelamento: this.motivoCancelamento,
      convite_enviado_em: this.conviteEnviadoEm,
      informacoes_preenchidas_em: this.informacoesPreenchidasEm,
      documento_confidencialidade_recebido_em: this.documentoConfidencialidadeRecebidoEm,
      confirmacao_presenca_visitante_em: this.confirmacaoPresencaVisitanteEm,
      agendamento_sala_efetuado_em: this.agendamentoSalaEfetuadoEm,
      necessidades_logisticas_encaminhadas_em: this.necessidadesLogisticasEncaminhadasEm,
    };
  }

  static fromApi(obj) {
    if (!obj) return null;
    const visitantes = Array.isArray(obj.visitantes) && obj.visitantes.length > 0
      ? obj.visitantes.map((v) => {
          const docs = normalizarVisitanteDocumentos(v);
          return {
          nomeCompleto: v.nome_completo ?? v.nomeCompleto ?? "",
          cpf: docs.cpf,
          rg: docs.rg,
          documentoRgCnh: docs.rg,
          email: v.email ?? "",
          telefoneWhatsapp: v.telefone_whatsapp ?? v.telefoneWhatsapp ?? "",
          fotoUrl: v.foto_url ?? v.fotoUrl ?? v.foto ?? v.path_image ?? v.pathImage ?? "",
          visitanteId: v.visitante_id != null ? Number(v.visitante_id) : v.visitanteId != null ? Number(v.visitanteId) : null,
        };
        })
      : null;
    const docsTopo = normalizarVisitanteDocumentos(obj);
    return new SolicitacaoVisita({
      id: obj.id,
      agendada: obj.agendada,
      criadoPorUsuarioId: obj.criado_por_usuario_id ?? null,
      nomeAnfitriao: obj.nome_anfitriao,
      anfitriaoUsuarioId: obj.anfitriao_usuario_id ?? null,
      areaDepartamento: obj.area_departamento,
      dataSolicitacao: obj.data_solicitacao,
      nomeCompleto: obj.nome_completo,
      cpf: obj.cpf ?? docsTopo.cpf,
      rg: obj.rg ?? docsTopo.rg,
      documentoRgCnh: obj.documento_rg_cnh ?? docsTopo.rg,
      empresaOrigem: obj.empresa_origem,
      email: obj.email,
      telefoneWhatsapp: obj.telefone_whatsapp,
      fotoUrl: obj.foto_url ?? obj.fotoUrl ?? obj.foto ?? obj.path_image ?? obj.pathImage ?? "",
      visitantes,
      tipoCliente: obj.tipo_cliente,
      tipoFornecedor: obj.tipo_fornecedor,
      tipoPrestadorServico: obj.tipo_prestador_servico,
      tipoParceiros: obj.tipo_parceiros,
      tipoOutros: obj.tipo_outros,
      tipoOutrosDescricao: obj.tipo_outros_descricao,
      motivoVisita: obj.motivo_visita,
      observacaoVisita: obj.observacao_visita ?? obj.observacaoVisita ?? "",
      areaSetorVisitado: obj.area_setor_visitado,
      dataVisita: obj.data_visita ?? obj.dataVisita ?? null,
      dataVisitaFim: obj.data_visita_fim ?? obj.dataVisitaFim ?? null,
      horarioEntradaPrevisto: obj.horario_entrada_previsto,
      horarioSaidaPrevisto: obj.horario_saida_previsto,
      quantidadeVisitantes: obj.quantidade_visitantes,
      visitaraFabrica: obj.visitara_fabrica,
      salaReuniaoLite: obj.sala_reuniao_lite,
      salaReuniaoHard: obj.sala_reuniao_hard,
      cafe: obj.cafe,
      agua: obj.agua,
      paoQueijoBiscoito: obj.pao_queijo_biscoito,
      brinde: obj.brinde,
      statusAgendamento: obj.status_agendamento,
      origem: obj.origem,
      cancelada: obj.cancelada ?? false,
      canceladaEm: obj.cancelada_em ?? null,
      motivoCancelamento: obj.motivo_cancelamento ?? "",
      conviteEnviadoEm: obj.convite_enviado_em ?? null,
      informacoesPreenchidasEm: obj.informacoes_preenchidas_em ?? null,
      documentoConfidencialidadeRecebidoEm: obj.documento_confidencialidade_recebido_em ?? null,
      confirmacaoPresencaVisitanteEm: obj.confirmacao_presenca_visitante_em ?? null,
      agendamentoSalaEfetuadoEm: obj.agendamento_sala_efetuado_em ?? null,
      necessidadesLogisticasEncaminhadasEm: obj.necessidades_logisticas_encaminhadas_em ?? null,
      checkinEm: obj.checkin_em ?? obj.checkinEm ?? null,
      checkoutEm: obj.checkout_em ?? obj.checkoutEm ?? null,
      checkoutRegistradoPorNome: obj.checkout_registrado_por_nome ?? obj.checkoutRegistradoPorNome ?? null,
      created_at: obj.created_at,
      updated_at: obj.updated_at,
    });
  }
}
